<script lang="ts" setup>
	import * as yup from 'yup';
	import { useField } from 'vee-validate';

	interface InputProps {
		id: string;
		name: string;
		placeholder?: string;
		modelValue?: number;
		isRequired?: boolean;
		label?: string;
		minValue?: number;
		maxValue?: number;
		step?: number;
	}

	const props = defineProps<InputProps>();
	const emits = defineEmits(['change', 'update:modelValue']);

	const isFocused = ref(false);

	const step = computed(() => (!props.step ? 1 : props.step));

	const minValue = computed(() => (!props.minValue ? 1 : props.minValue));

	const maxValue = computed(() => (!props.maxValue ? 999 : props.maxValue));

	const rules = computed(() => {
		let rules = yup
			.number()
			.typeError('Укажите цифры')
			.min(minValue.value, `Введите число больше ${minValue.value}`)
			.max(maxValue.value, `Введите число меньше ${maxValue.value}`);

		if (props.isRequired) rules = rules.required('Введите цифры');

		return rules;
	});

	const { value, errorMessage } = useField(() => props.name, rules.value, {
		initialValue: props.modelValue,
		syncVModel: true,
	});

	const onChange = () => {
		emits('change');
	};

	const onFocus = () => {
		isFocused.value = true;
	};

	const onBlur = () => {
		isFocused.value = false;
	};

	const onPlus = () => {
		if (!value.value) {
			value.value = step.value;
		} else if (value.value >= maxValue.value) {
			value.value = maxValue.value;
		} else {
			value.value = Number(value.value) + Number(step.value);
		}
		nextTick(() => {
			emits('change', value.value);
		});
	};

	const onMinus = () => {
		if (!value.value || value.value < minValue.value + 1) {
			value.value = minValue.value;
			return;
		} else if (value.value > maxValue.value) {
			value.value = maxValue.value;
			return;
		} else {
			value.value = Number(value.value) - Number(step.value);
		}
		nextTick(() => {
			emits('change', value.value);
		});
	};
</script>

<template>
	<div class="inputs-block__quantity-block">
		<button
			type="button"
			class="quantity-block__button"
			aria-label="уменьшить значение"
			@click.prevent="onMinus"
		>
			<Icon name="local:minus" />
		</button>
		<UiInputMask
			:id="id"
			v-model="value"
			:name="name"
			label="Количество"
			:error="errorMessage"
			:required-field="true"
			type="number"
			@change="onChange"
			@focus="onFocus"
			@blur="onBlur"
		/>
		<button
			type="button"
			class="quantity-block__button"
			aria-label="увеличить значение"
			@click.prevent="onPlus"
		>
			<Icon name="local:plus" />
		</button>
	</div>
</template>

<style lang="scss" scoped>
	.inputs-block__quantity-block {
		display: grid;
		grid-template-columns: 42px 1fr 42px;
		align-items: end;
		gap: 7px;
		.quantity-block__button {
			cursor: pointer;
			display: flex;
			justify-content: center;
			align-items: center;
			padding: 4px;
			height: 42px;
			width: 100%;
			background-color: var(--color-grey);
			border-radius: 7px;
			transition: background-color ease-in-out 0.2s, color ease-in-out 0.2s;
			&:hover,
			&:focus-visible {
				color: var(--color-white);
				background-color: var(--color-grey-dark);
			}
		}
		:deep(.app-input-wrapper .app-input-label) {
			position: relative;
			left: -49px;
		}
		:deep(.app-input-wrapper .app-input-element) {
			text-align: center;
		}
	}
</style>
